import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Map as CarteGL, MapMouseEvent } from 'maplibre-gl';
import { versUtm } from '../../src/partage/utm.js';
import { libelleOrigineParcelle, libelleStatutParcelle, surfaceEnFrancais } from '../../src/partage/libelles';
import { api, ErreurApi } from './api';
import { useAnnoncer } from './annonces';

/**
 * LA TABLE ATTRIBUTAIRE — le bas de l'atelier.
 *
 * ⚠️⚠️ CE QUI SÉPARE UN ATELIER SIG D'UNE CARTE CLIQUABLE. Une carte rend UN
 * objet à la fois, celui qu'on vient de cliquer. Un atelier tient une
 * SÉLECTION : plusieurs objets, comparables ligne à ligne, triables,
 * dénombrables, et dont on peut lire la somme. C'est la différence entre
 * « regarder une parcelle » et « instruire un lot ».
 *
 * ⚠️ LES COLONNES SONT DÉRIVÉES DES OBJETS, PAS DÉCLARÉES. Les propriétés
 * servies par la tuile changent avec `tuiles.ts` ; une liste de colonnes écrite
 * ici deviendrait fausse en silence — une colonne disparue s'afficherait vide,
 * une colonne neuve resterait invisible. On lit les clés réellement présentes
 * et on ordonne celles qu'on sait nommer.
 *
 * ⚠️ ET LA SÉLECTION NE VIENT PAS DES TUILES POUR LA GÉOMÉTRIE. On affiche ici
 * les ATTRIBUTS, qui sont exacts dans la tuile. Toute mesure géométrique tirée
 * d'une tuile serait fausse au bord : `ST_AsMVTGeom(..., clip=true)` découpe
 * les objets à la frontière de la dalle. La surface affichée vient donc de la
 * propriété `surface_m2` servie par le SQL, jamais d'un calcul sur le rendu.
 */

/** Ce qu'on sait nommer, dans l'ordre où un agent le lit. */
const COLONNES_CONNUES: Array<{ cle: string; titre: string; aligne?: 'droite' }> = [
  { cle: 'identifiant', titre: 'Identifiant' },
  { cle: 'numero_cadastral', titre: 'N° plan' },
  { cle: 'section', titre: 'Section' },
  { cle: 'statut', titre: 'Statut' },
  { cle: 'origine', titre: 'Origine' },
  { cle: 'surface_m2', titre: 'Surface', aligne: 'droite' },
  { cle: 'precision_m', titre: 'Précision', aligne: 'droite' },
  { cle: 'verifiee', titre: 'Vérifiée' },
];

export interface LigneSelection {
  id: string;
  couche: string;
  proprietes: Record<string, unknown>;
  /** Le point cliqué — ce qui permet de recentrer sur la ligne. */
  lon: number;
  lat: number;
}

function formater(cle: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (cle === 'surface_m2') return surfaceEnFrancais(v);
  if (cle === 'precision_m') return `${Number(v).toLocaleString('fr-FR')} m`;
  /* ⚠️ UN BOOLÉEN TOMBAIT DANS `String(v)` ET SORTAIT « false ». Dans une
     interface qui n'est qu'en français, la colonne « Vérifiée » annonçait donc
     `false` — un mot qu'aucun agent n'a à traduire de tête. Les tuiles MVT
     rendent tantôt un booléen, tantôt sa forme textuelle : les deux passent. */
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (v === 'true' || v === 'false') return v === 'true' ? 'oui' : 'non';
  /* Les deux énumérations de la parcelle ont leur table depuis `libelles.ts` ;
     les afficher brutes ferait lire « derive_bati » ici et « Dérivée du bâti »
     dans la fiche, pour la même valeur. */
  if (cle === 'origine') return libelleOrigineParcelle(v);
  if (cle === 'statut') return libelleStatutParcelle(v);
  return String(v);
}

export function TableAttributaire(
  { carte, actif, onOuvrir, onSelectionParcelles, imposees = null }:
  {
    carte: CarteGL | null; actif: boolean; onOuvrir?: (identifiant: string) => void;
    /**
     * LA SÉLECTION SE REFLÈTE SUR LA CARTE — D.948. Sans ce fil, une ligne
     * retenue par erreur (Maj+clic raté) partait dans l'export sans qu'aucune
     * surbrillance ne la montre. Rappelé à CHAQUE changement, liste vide
     * comprise : un reflet qui survit à la sélection mentirait.
     */
    onSelectionParcelles?: (identifiants: string[]) => void;
    /**
     * Des lignes IMPOSÉES de l'extérieur — le chemin CLAVIER de la sélection
     * (« retenir les parcelles de la section », depuis l'inspecteur). Adoptées
     * sur leur clé, FUSIONNÉES sans doublon : imposer ne doit pas détruire ce
     * que la main avait retenu.
     */
    imposees?: { cle: string; lignes: LigneSelection[] } | null;
  },
) {
  const [lignes, setLignes] = useState<LigneSelection[]>([]);
  const [ouverte, setOuverte] = useState(false);
  const [tri, setTri] = useState<{ cle: string; sens: 1 | -1 } | null>(null);
  /** Le filtre — la carte sait chercher, la table doit savoir aussi (D.948). */
  const [filtre, setFiltre] = useState('');
  const annoncer = useAnnoncer();

  /* Les lignes imposées, adoptées sur leur clé. */
  const cleImposee = imposees?.cle ?? null;
  useEffect(() => {
    if (!imposees) return;
    setLignes((prec) => {
      const vus = new Set(prec.map((l) => l.id));
      return [...prec, ...imposees.lignes.filter((l) => !vus.has(l.id))];
    });
    setOuverte(true);
  }, [cleImposee]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!carte || !actif) return;

    const surClic = (e: MapMouseEvent) => {
      const trouvees = carte.queryRenderedFeatures(e.point, {
        layers: ['parcelles-remplissage', 'batiments'],
      });
      const f = trouvees[0];
      if (!f) return;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      const id = String(props.identifiant ?? props.id ?? f.id ?? '');
      if (!id) return;

      setLignes((prec) => {
        /**
         * ⚠️ MAJ/⌘ AJOUTE, UN CLIC SEUL REMPLACE — la convention de tous les
         * ateliers. L'inverse (accumuler toujours) remplit la table d'objets
         * qu'on n'a fait que survoler en cherchant le bon, et l'agent ne sait
         * plus ce qu'il a réellement retenu.
         */
        const cumul = e.originalEvent.shiftKey || e.originalEvent.metaKey || e.originalEvent.ctrlKey;
        const ligne: LigneSelection = {
          id, couche: String(f.sourceLayer ?? f.layer?.id ?? ''), proprietes: props,
          lon: e.lngLat.lng, lat: e.lngLat.lat,
        };
        if (!cumul) return [ligne];
        /* Recliquer un objet déjà retenu le RETIRE : c'est ainsi qu'on corrige
           une sélection sans tout recommencer. */
        return prec.some((l) => l.id === id)
          ? prec.filter((l) => l.id !== id)
          : [...prec, ligne];
      });
      setOuverte(true);
    };

    carte.on('click', surClic);
    return () => { carte.off('click', surClic); };
  }, [carte, actif]);

  /** Les colonnes réellement portées par la sélection. */
  const colonnes = useMemo(() => {
    const presentes = new Set<string>();
    for (const l of lignes) for (const k of Object.keys(l.proprietes)) presentes.add(k);
    const connues = COLONNES_CONNUES.filter((c) => presentes.has(c.cle));
    /* ⚠️ LES CLÉS QU'ON NE SAIT PAS NOMMER SONT MONTRÉES QUAND MÊME, après les
       autres. Les filtrer cacherait à l'agent ce que la tuile porte vraiment —
       et c'est précisément quand une colonne inattendue apparaît qu'il faut la
       voir. */
    const inconnues: typeof COLONNES_CONNUES = [...presentes]
      .filter((k) => !COLONNES_CONNUES.some((c) => c.cle === k))
      .sort()
      .map((k) => ({ cle: k, titre: k }));
    return [...connues, ...inconnues];
  }, [lignes]);

  /* Le filtre cherche dans les valeurs FORMATÉES : l'agent tape ce qu'il
     lit (« dérivée du bâti »), pas la valeur brute de la tuile. */
  const filtrees = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    if (!f) return lignes;
    return lignes.filter((l) =>
      l.id.toLowerCase().includes(f)
      || Object.entries(l.proprietes).some(([k, v]) => formater(k, v).toLowerCase().includes(f)));
  }, [lignes, filtre]);

  const triees = useMemo(() => {
    if (!tri) return filtrees;
    return [...filtrees].sort((a, b) => {
      const va = a.proprietes[tri.cle];
      const vb = b.proprietes[tri.cle];
      const na = Number(va);
      const nb = Number(vb);
      /* Numérique quand les deux le sont, alphabétique sinon : trier « 100 »
         avant « 20 » parce que ce sont des chaînes est le défaut classique
         d'une table attributaire. */
      if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * tri.sens;
      return String(va ?? '').localeCompare(String(vb ?? ''), 'fr') * tri.sens;
    });
  }, [filtrees, tri]);

  /* Le reflet vers la carte et l'ANNONCE — une seule fois par changement
     (D.949) : c'est la région unique qui parle, jamais deux composants. */
  /* ⚠️ Clé = les IDENTIFIANTS, pas le compte : remplacer une parcelle par une
     autre garde le même compte, et un changement muet est exactement ce que
     D.949 interdit (« chaque changement de sélection, une seule fois »). */
  const dejaDit = useRef<string | null>(null);
  useEffect(() => {
    onSelectionParcelles?.(lignes.filter((l) => l.couche === 'parcelles').map((l) => l.id));
    const cle = lignes.map((l) => l.id).join('|');
    if (dejaDit.current !== null && dejaDit.current !== cle) {
      annoncer(lignes.length === 0
        ? 'Sélection vidée.'
        : lignes.length === 1
          ? `Sélection : ${lignes[0]!.id}.`
          : `${lignes.length} objets dans la sélection.`);
    }
    dejaDit.current = cle;
  }, [lignes, onSelectionParcelles, annoncer]);

  /** Retirer UNE ligne — depuis la table, au clavier comme à la souris. */
  const retirer = useCallback((id: string) => {
    setLignes((prec) => prec.filter((l) => l.id !== id));
  }, []);

  /** La somme des surfaces — ce qu'un agent cherche en sélectionnant un lot. */
  const surfaceTotale = useMemo(
    () => lignes.reduce((s, l) => s + (Number(l.proprietes.surface_m2) || 0), 0),
    [lignes],
  );

  /**
   * SÉLECTIONNER PAR EMPRISE — le geste que tout atelier SIG porte, et que
   * celui-ci n'avait pas.
   *
   * ⚠️⚠️ ON DEMANDE AU SERVEUR, ON NE LIT PAS LES TUILES. `queryRenderedFeatures`
   * sur un rectangle serait le réflexe, et il serait faux ici pour trois
   * raisons qui se cumulent : il ne rend QUE ce qui est dessiné (une parcelle
   * hors du cadre ou pas encore peinte est absente), il rend la MÊME parcelle
   * plusieurs fois quand elle chevauche une couture de tuile, et
   * `ST_AsMVTGeom(..., true)` DÉCOUPE au bord — les entités y sont tronquées.
   *
   * Sur une sélection qui ne fait qu'éclairer un objet, ce serait tolérable.
   * Celle-ci alimente l'EXPORT CSV par identifiant : un manque silencieux
   * produirait un fichier incomplet que rien ne signale, et un doublon
   * compterait deux fois la même parcelle. `GET /parcelles?ouest=…` rend le jeu
   * exact, depuis la base. C'est la même raison qui fait que l'outil de tracé
   * refuse les tuiles pour son accrochage.
   */
  const [modeEmprise, setModeEmprise] = useState(false);
  const [empriseEnCours, setEmpriseEnCours] = useState(false);
  const boite = useRef<HTMLDivElement | null>(null);

  const selectionnerEmprise = useCallback(async (b: {
    ouest: number; sud: number; est: number; nord: number;
  }) => {
    setEmpriseEnCours(true);
    setExportDit(null);
    try {
      /* ⚠️ SANS LA GÉOMÉTRIE, ET LA LIMITE EST BAISSÉE À 500. Mesuré : 2 000
         entités avec géométrie font 3,34 Mo ; le service les rend en 0,15 s,
         mais dans un vrai navigateur l'en-tête 200 arrive et le corps ne finit
         jamais d'arriver — le bouton reste éteint, et rien ne dit pourquoi.
         Sans géométrie, la même sélection pèse 20 % de cela. Et 500 lignes
         sont déjà bien plus qu'un agent n'examine à l'œil : au-delà, la
         troncature le DIT et il resserre son rectangle. */
      const c = await api.parcelles.dansEnveloppe(b, 500, true);
      const lignes: LigneSelection[] = (c.features ?? []).map((f: any) => ({
        id: String(f.properties?.identifiant ?? f.id ?? ''),
        couche: 'parcelles',
        proprietes: (f.properties ?? {}) as Record<string, unknown>,
        /* Le centre de l'enveloppe : la ligne doit pouvoir se recentrer, et
           une parcelle prise par emprise n'a pas de « point cliqué ». */
        lon: (b.ouest + b.est) / 2,
        lat: (b.sud + b.nord) / 2,
      })).filter((l) => l.id !== '');
      setLignes(lignes);
      setOuverte(true);
      /**
       * ⚠️ LA TRONCATURE SE DIT. La route plafonne ; sans ce message, l'agent
       * exporterait 2 000 lignes en croyant tenir tout le rectangle — et
       * l'export serait exact sur ce qu'il contient, faux sur ce qu'il promet.
       */
      if (c.tronque) {
        setExportDit(
          `⚠️ ${lignes.length} parcelle(s) retenue(s), mais l'emprise en contient davantage : `
          + `la limite du service (${c.limite ?? 500}) a mordu. Resserrez le rectangle.`);
      }
    } catch (e) {
      setExportDit(e instanceof ErreurApi ? e.message : "La sélection par emprise n'a pas abouti.");
    } finally {
      setEmpriseEnCours(false);
      setModeEmprise(false);
    }
  }, []);

  /**
   * LE RECTANGLE À L'ÉCRAN.
   *
   * ⚠️ `dragPan` ET `boxZoom` SONT COUPÉS PENDANT LE GESTE, puis REMIS. Sans
   * cela, tirer un rectangle déplace la carte sous le rectangle : les deux
   * coins désignent alors deux endroits qui n'ont jamais coexisté à l'écran, et
   * l'emprise obtenue n'est celle de personne.
   */
  useEffect(() => {
    if (!modeEmprise) return;
    /* Une porte de sortie au clavier : un mode qu'on ne peut quitter qu'en
       tirant un rectangle piège l'agent qui l'a ouvert par erreur. */
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') setModeEmprise(false); };
    window.addEventListener('keydown', surTouche);
    return () => { window.removeEventListener('keydown', surTouche); };
  }, [modeEmprise]);

  useEffect(() => {
    if (!carte || !modeEmprise) return;
    const conteneur = carte.getContainer();
    const dragPanEtait = carte.dragPan.isEnabled();
    const boxZoomEtait = carte.boxZoom.isEnabled();
    carte.dragPan.disable();
    carte.boxZoom.disable();
    conteneur.style.cursor = 'crosshair';

    let depart: { x: number; y: number } | null = null;

    const rect = () => conteneur.getBoundingClientRect();
    const surBas = (e: MouseEvent) => {
      const r = rect();
      depart = { x: e.clientX - r.left, y: e.clientY - r.top };
      const d = document.createElement('div');
      d.className = 'table-attr-boite';
      conteneur.appendChild(d);
      boite.current = d;
    };
    const surMouvement = (e: MouseEvent) => {
      if (!depart || !boite.current) return;
      const r = rect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      Object.assign(boite.current.style, {
        left: `${Math.min(depart.x, x)}px`, top: `${Math.min(depart.y, y)}px`,
        width: `${Math.abs(x - depart.x)}px`, height: `${Math.abs(y - depart.y)}px`,
      });
    };
    const surHaut = (e: MouseEvent) => {
      const d = depart; depart = null;
      boite.current?.remove(); boite.current = null;
      if (!d) return;
      const r = rect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      /* ⚠️ UN CLIC N'EST PAS UNE EMPRISE. Sans ce seuil, un clic accidentel
         rendrait un rectangle de zéro pixel — et `ouest < est` serait faux,
         donc le service rendrait 400 sur un geste que l'agent n'a pas fait. */
      if (Math.abs(x - d.x) < 4 || Math.abs(y - d.y) < 4) { setModeEmprise(false); return; }
      const a = carte.unproject([Math.min(d.x, x), Math.min(d.y, y)]);
      const b = carte.unproject([Math.max(d.x, x), Math.max(d.y, y)]);
      void selectionnerEmprise({
        ouest: Math.min(a.lng, b.lng), est: Math.max(a.lng, b.lng),
        sud: Math.min(a.lat, b.lat), nord: Math.max(a.lat, b.lat),
      });
    };

    conteneur.addEventListener('mousedown', surBas);
    window.addEventListener('mousemove', surMouvement);
    window.addEventListener('mouseup', surHaut);
    return () => {
      conteneur.removeEventListener('mousedown', surBas);
      window.removeEventListener('mousemove', surMouvement);
      window.removeEventListener('mouseup', surHaut);
      boite.current?.remove(); boite.current = null;
      conteneur.style.cursor = '';
      if (dragPanEtait) carte.dragPan.enable();
      if (boxZoomEtait) carte.boxZoom.enable();
    };
  }, [carte, modeEmprise, selectionnerEmprise]);

  /**
   * EXPORTER CE QUE LA TABLE RETIENT.
   *
   * ⚠️⚠️ LE SERVICE SAVAIT EXPORTER ET AUCUN ÉCRAN NE L'APPELAIT. Mesuré le
   * 23/09/2026 : `POST /exports/parcelles.csv` existait, éprouvé, et
   * `grep -rl "exports/parcelles" frontend/src` rendait **ZÉRO** — le même
   * motif « présent et inatteignable » que `verifier-emprise` la veille. Le
   * bouton vit ICI et non dans un panneau « Outils » : exporter une table,
   * c'est l'acte de la table, et l'éloigner de sa sélection obligerait à
   * réexpliquer laquelle des deux on exporte.
   *
   * ⚠️ SEULES LES PARCELLES PARTENT. La table tient aussi des bâtiments, et la
   * route n'exporte que `cad.parcelle`. On filtre, et on le DIT : un export
   * silencieusement amputé ferait conclure que les bâtiments sélectionnés
   * n'existent pas au registre.
   */
  const [exportEnCours, setExportEnCours] = useState(false);
  const [exportDit, setExportDit] = useState<string | null>(null);
  /* Ce qui se dit en place s'ANNONCE aussi — par la région unique (D.949). */
  useEffect(() => { if (exportDit) annoncer(exportDit); }, [exportDit, annoncer]);

  const identifiantsParcelles = useMemo(
    () => lignes.filter((l) => l.couche === 'parcelles').map((l) => l.id),
    [lignes],
  );

  const exporter = useCallback(async () => {
    if (identifiantsParcelles.length === 0) return;
    setExportEnCours(true);
    setExportDit(null);
    try {
      const f = await api.exports.parcellesCsv({
        identifiants: identifiantsParcelles,
        nom: `selection-${identifiantsParcelles.length}-parcelles`,
      });
      /* ⚠️ LE NAVIGATEUR ENREGISTRE, LE SERVICE NOMME. Le nom vient de
         `Content-Disposition` : un nom fabriqué ici divergerait de celui que le
         journal a consigné, et deux traces du même export porteraient deux
         noms. */
      const url = URL.createObjectURL(new Blob([f.contenu], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = f.nom;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      /* ⚠️ ON ANNONCE CE QUE LE FICHIER PORTE, PAS CE QU'ON A DEMANDÉ.
         `X-Export-Lignes` est relu DANS le CSV par le service ; l'ignorer
         ferait promettre un décompte que le fichier peut démentir. */
      const batiments = lignes.length - identifiantsParcelles.length;
      setExportDit(
        `${f.nom} — ${f.lignes ?? identifiantsParcelles.length} ligne(s)`
        + (f.tronque ? ' ⚠️ tronqué : la limite du service a mordu.' : '')
        + (batiments > 0 ? ` ${batiments} bâtiment(s) écarté(s) : l'export porte les parcelles.` : ''));
    } catch (e) {
      setExportDit(e instanceof ErreurApi ? e.message : "L'export n'a pas abouti.");
    } finally {
      setExportEnCours(false);
    }
  }, [identifiantsParcelles, lignes.length]);

  if (!actif) return null;

  return (
    <div className={`table-attr${ouverte && lignes.length > 0 ? ' ouverte' : ''}`}>
      <div className="table-attr-tete">
        <button type="button" className="table-attr-bascule"
          onClick={() => setOuverte(!ouverte)} aria-expanded={ouverte}>
          <span>Sélection</span>
          <span className="table-attr-compte">{lignes.length}</span>
        </button>

        {lignes.length > 0 && (
          <>
            {/* ⚠️ LA SOMME EST AFFICHÉE AVEC SON COMPTE. « 4,2 ha » seul ne dit
                pas sur combien d'objets, et deux sélections très différentes
                peuvent donner la même surface. */}
            <span className="table-attr-somme">
              {surfaceTotale > 0 && (
                <>Surface cumulée : <strong>
                  {surfaceTotale >= 10_000
                    ? `${(surfaceTotale / 10_000).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} ha`
                    : `${surfaceTotale.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m²`}
                </strong> sur {lignes.length} objet(s)</>
              )}
            </span>
            {/* ⚠️ PAS DE BOUTON ÉTEINT QUAND IL N'Y A QUE DES BÂTIMENTS :
                une phrase. Un « Exporter » grisé fait chercher ce qui manque
                dans la sélection, alors que ce qui manque est une route du
                service pour cette couche-là. */}
            {identifiantsParcelles.length > 0 ? (
              <button type="button" className="table-attr-exporter"
                onClick={() => { void exporter(); }} disabled={exportEnCours}>
                {exportEnCours
                  ? 'Export en cours…'
                  : `Exporter ${identifiantsParcelles.length} parcelle(s) en CSV`}
              </button>
            ) : (
              <span className="table-attr-sans-export">
                L'export tabulaire porte les parcelles ; cette sélection n'en contient aucune.
              </span>
            )}
            <button type="button" className="table-attr-vider"
              onClick={() => { setLignes([]); setExportDit(null); }}>
              Vider la sélection
            </button>
          </>
        )}

        {/* ⚠️ Plus de `role="status"` LOCAL : la région unique annonce (D.949),
            le texte reste lisible en place. */}
        {exportDit && (
          <span className="table-attr-export-dit">{exportDit}</span>
        )}

        {/* ⚠️ CE BOUTON VIT HORS DU `lignes.length > 0`, et c'est tout son
            intérêt : on sélectionne par emprise QUAND ON N'A RIEN. Le ranger
            avec « Exporter » et « Vider » l'aurait rendu invisible au seul
            moment où il sert. */}
        <button type="button"
          className={`table-attr-emprise${modeEmprise ? ' actif' : ''}`}
          onClick={() => setModeEmprise((v) => !v)}
          disabled={empriseEnCours || !carte}
          aria-pressed={modeEmprise}>
          {empriseEnCours ? 'Interrogation…'
            : modeEmprise ? 'Tirez le rectangle (échap pour annuler)'
            : 'Sélectionner une emprise'}
        </button>

        {lignes.length === 0 && (
          <span className="table-attr-aide">
            Cliquez une parcelle · <kbd>Maj</kbd> + clic pour en ajouter
          </span>
        )}
      </div>

      {ouverte && lignes.length > 0 && (
        <div className="table-attr-corps">
          {/* ⚠️ LE FILTRE — ce que la carte permet (chercher), la table le
              permet aussi, au clavier (D.948). Il cherche dans les valeurs
              FORMATÉES, celles que l'agent lit. */}
          <input className="table-attr-filtre" value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder="filtrer la sélection — ex. dérivée du bâti"
            aria-label="Filtrer les lignes de la sélection" />
          {filtre.trim() !== '' && (
            <span className="mention"> {triees.length} sur {lignes.length} ligne(s)</span>
          )}
          <table>
            {/* ⚠️ `caption` + `scope` — D.948 : sans eux, un lecteur d'écran
                lit une grille de nombres sans savoir ce qu'elle est ni à quoi
                chaque colonne se rapporte. */}
            <caption className="visuellement-cache">
              Sélection de l’atelier — {lignes.length} objet(s), colonnes triables
            </caption>
            <thead>
              <tr>
                <th scope="col" className="col-rang">#</th>
                {colonnes.map((c) => (
                  <th key={c.cle} scope="col"
                      className={c.aligne === 'droite' ? 'droite' : undefined}
                      aria-sort={tri?.cle === c.cle
                        ? (tri.sens === 1 ? 'ascending' : 'descending') : 'none'}>
                    <button type="button" onClick={() => setTri(
                      tri?.cle === c.cle ? { cle: c.cle, sens: tri.sens === 1 ? -1 : 1 } : { cle: c.cle, sens: 1 },
                    )}>
                      {c.titre}
                      {tri?.cle === c.cle && <span aria-hidden="true">{tri.sens === 1 ? ' ▲' : ' ▼'}</span>}
                    </button>
                  </th>
                ))}
                <th scope="col" className="droite">UTM</th>
                <th scope="col"><span className="visuellement-cache">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {triees.map((l, i) => {
                const u = versUtm(l.lon, l.lat);
                const aller = () => {
                  /* ⚠️ Saut sans animation — refus D.951. */
                  carte?.jumpTo({ center: [l.lon, l.lat], zoom: Math.max(carte.getZoom(), 17) });
                  onOuvrir?.(String(l.proprietes.identifiant ?? l.id));
                };
                return (
                  /* ⚠️ LA LIGNE EST UN ARRÊT DU CLAVIER — D.948 : Entrée y va
                     et ouvre, Suppr retire. Le focus visible vient de la
                     règle globale de socle.css. */
                  <tr key={l.id} tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter') { e.preventDefault(); aller(); }
                        if (e.key === 'Delete' || e.key === 'Backspace') {
                          e.preventDefault(); retirer(l.id);
                        }
                      }}>
                    <td className="col-rang">{i + 1}</td>
                    {colonnes.map((c) => (
                      <td key={c.cle} className={c.aligne === 'droite' ? 'droite mono' : undefined}>
                        {formater(c.cle, l.proprietes[c.cle])}
                      </td>
                    ))}
                    <td className="droite mono">
                      {Math.round(u.x).toLocaleString('fr-FR')} · {Math.round(u.y).toLocaleString('fr-FR')}
                      <em className="table-attr-zone"> z{u.zone}</em>
                    </td>
                    <td>
                      <button type="button" className="lien" onClick={aller}>
                        Aller
                      </button>
                      {' '}
                      {/* ⚠️ Retirer SE VOIT — recliquer l'objet sur la carte
                          retirait aussi, mais depuis la table c'était
                          impossible, et au clavier doublement. */}
                      <button type="button" className="lien" onClick={() => retirer(l.id)}>
                        Retirer <kbd>Suppr</kbd>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
